import { useHistory, useLocation } from "react-router-dom";
import { useEffectOnce } from "./useEffectOnce";

/**
 * The current URL with a one-shot `?<param>=1` flag removed, every OTHER query
 * param (e.g. a selected `room`/`topic` tab) preserved. Pure — exported for unit
 * test.
 */
export function urlWithoutQueryFlag(
  pathname: string,
  search: string,
  param: string
): string {
  const params = new URLSearchParams(search);
  params.delete(param);
  const query = params.toString();
  return query === "" ? pathname : `${pathname}?${query}`;
}

/**
 * Read a one-shot `?<param>=1` deep-link flag AND consume it: on first mount we
 * strip it from the URL (`history.replace` of the current entry) before
 * returning. The consuming component latches the flag into its own `useState`
 * from this first render, so clearing the URL does not close what it opened —
 * but a later back/forward navigation to the same history entry (or a remount)
 * then finds no flag and won't re-open something the user already dismissed.
 *
 * Used by the discovery feed's push-nudge deep-link `openInterests` (the simple
 * topic picker). Its sibling `openPreferences` (the agent-assisted profiling
 * conversation) is NOT consumed here anymore — it is a router-level redirect to
 * `/onboarding/interests`, since that conversation is a page, not a sheet.
 * The returned boolean is TRUE only on the first render after arrival.
 */
export function useConsumeQueryFlag(param: string): boolean {
  const history = useHistory();
  const location = useLocation();
  const isSet = new URLSearchParams(location.search).get(param) === "1";
  useEffectOnce(() => {
    if (!isSet) return;
    history.replace(
      urlWithoutQueryFlag(location.pathname, location.search, param)
    );
  });
  return isSet;
}
